// Shared pieces for the Commissions screens (CEO rule 2026-10-14: full width,
// large readable type — body 15px, numbers 15px+, headers 13px).
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";

export type PillTone = "active" | "prospect" | "dormant" | "closed" | "warn" | "info";

/** Status word in a tinted badge — readable size, words kept as written. */
export function Pill({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return (
    <StatusPill tone={tone} className="py-1 text-[13px] normal-case">
      {children}
    </StatusPill>
  );
}

export const T_WRAP = "overflow-x-auto rounded-md border border-rule bg-white";
export const T_HEAD = "border-b border-[#e7e7e4] bg-[#fafaf8] text-left text-[13px] font-medium text-muted";
export const T_TH = "px-4 py-3 font-medium whitespace-nowrap";
export const T_TD = "px-4 py-3.5 text-[15px] text-ink align-top";
export const T_NUM = "px-4 py-3.5 text-right text-[15px] tabular-nums text-ink align-top whitespace-nowrap";
export const T_ROW = "border-b border-[#e7e7e4] last:border-0";
export const T_ROW_CLICK = "border-b border-[#e7e7e4] last:border-0 cursor-pointer hover:bg-[#fafaf8]";

export const INPUT =
  "h-11 w-full rounded-md border border-rule bg-white px-3 text-[15px] text-ink focus:outline-none focus:ring-2 focus:ring-[#63768b]/40";
export const TEXTAREA =
  "w-full rounded-md border border-rule bg-white px-3 py-2 text-[15px] text-ink focus:outline-none focus:ring-2 focus:ring-[#63768b]/40";

export function Section({
  title,
  intro,
  action,
  children,
  className,
}: {
  title: ReactNode;
  intro?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("mb-8", className)}>
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[19px] font-semibold text-ink">{title}</h2>
          {intro ? <p className="mt-1 max-w-4xl text-[15px] text-muted">{intro}</p> : null}
        </div>
        {action ? <div className="flex shrink-0 flex-wrap items-center gap-2">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function Notice({
  tone = "info",
  children,
  className,
}: {
  tone?: "info" | "warn" | "ok" | "risk";
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === "risk" ? "alert" : undefined}
      className={cn(
        "rounded-md border px-4 py-3 text-[15px]",
        tone === "info" && "border-rule bg-white text-ink",
        tone === "warn" && "border-[#ecd9b5] bg-warn-bg text-warn",
        tone === "ok" && "border-[#cfe3d5] bg-ok-bg text-ok",
        tone === "risk" && "border-[#efcfcb] bg-risk-bg text-risk",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-[14px] font-medium text-ink">{label}</span>
      {children}
      {hint ? <span className="text-[13px] text-muted">{hint}</span> : null}
    </label>
  );
}

export interface TileData {
  label: string;
  value: string | number | null;
  note?: ReactNode;
  attention?: boolean;
  onClick?: () => void;
}

export function Tiles({ tiles }: { tiles: TileData[] }) {
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-rule bg-rule md:grid-cols-3 xl:grid-cols-5">
      {tiles.map((t) => {
        const body = (
          <>
            <span className="text-[14px] text-muted">{t.label}</span>
            {t.value === null ? (
              <span className="mt-2 h-8 w-20 animate-pulse rounded bg-canvas" aria-hidden />
            ) : (
              <span
                className={cn(
                  "mt-1.5 text-[30px] font-medium leading-tight tracking-[-0.6px] tabular-nums",
                  t.attention ? "text-warn" : "text-ink",
                )}
              >
                {t.value}
              </span>
            )}
            {t.note ? <span className="mt-1.5 text-[13px] text-muted">{t.note}</span> : null}
          </>
        );
        return t.onClick ? (
          <button
            key={t.label}
            type="button"
            onClick={t.onClick}
            className="flex flex-col bg-white p-5 text-left transition-colors hover:bg-[#fafaf8]"
          >
            {body}
          </button>
        ) : (
          <div key={t.label} className="flex flex-col bg-white p-5">
            {body}
          </div>
        );
      })}
    </div>
  );
}

/** A right-hand panel over the page (order / batch detail). Focus moves into
 *  it on open, Tab stays inside, Escape closes, and focus returns to whatever
 *  opened it. Labelled by its title. */
export function Drawer({ title, onClose, children }: { title: ReactNode; onClose: () => void; children: ReactNode }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div aria-hidden className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative flex h-full w-full max-w-[1100px] flex-col overflow-y-auto bg-canvas shadow-xl focus:outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-rule bg-white px-6 py-4">
          <h2 id={titleId} className="min-w-0 truncate text-[21px] font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-cream-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="px-6 py-6">{children}</div>
      </div>
    </div>
  );
}

/** The commission status pill, plus — in words, not a tooltip — what an
 *  estimated line is waiting on and any Finance review reason. */
export function LineStatusCell({
  status,
  label,
  tone,
  waitingOn,
  reviewText,
  reviewPill = "Needs review",
}: {
  status: string;
  label: string;
  tone: PillTone;
  waitingOn?: string[] | null;
  reviewText?: string | null;
  reviewPill?: string;
}) {
  return (
    <div className="flex max-w-[320px] flex-col items-start gap-1.5">
      <Pill tone={tone}>{label}</Pill>
      {status === "estimated" && waitingOn && waitingOn.length > 0 && (
        <span className="text-[14px] text-muted">Waiting on: {waitingOn.join(", ")}</span>
      )}
      {reviewText && (
        <>
          <Pill tone="warn">{reviewPill}</Pill>
          <span className="text-[14px] text-warn">{reviewText}</span>
        </>
      )}
    </div>
  );
}

/** Two-step action: the first click asks, the second does it. Optional
 *  short-reason box (Reject) must be filled before confirming. */
export function ConfirmAction({
  label,
  confirmLabel,
  question,
  onConfirm,
  disabled,
  variant = "primary",
  size = "sm",
  needReason,
}: {
  label: string;
  confirmLabel: string;
  question?: ReactNode;
  onConfirm: (reason?: string) => void;
  disabled?: boolean;
  variant?: "primary" | "outline" | "ghost";
  size?: "sm" | "md";
  needReason?: string;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  if (!asking) {
    return (
      <Button size={size} variant={variant} disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </Button>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-2">
      {question ? <span className="text-[14px] text-ink">{question}</span> : null}
      {needReason ? (
        <input
          className="h-9 w-56 rounded-md border border-rule bg-white px-2 text-[14px]"
          placeholder={needReason}
          aria-label={needReason}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
        />
      ) : null}
      <Button
        size={size}
        disabled={disabled || (!!needReason && reason.trim().length < 2)}
        onClick={() => {
          onConfirm(reason.trim() || undefined);
          setAsking(false);
          setReason("");
        }}
      >
        {confirmLabel}
      </Button>
      <Button size={size} variant="ghost" onClick={() => setAsking(false)}>
        Cancel
      </Button>
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-rule bg-white p-6 text-[15px] text-muted">{children}</p>;
}

export function Loading() {
  return <p className="p-6 text-[15px] text-muted">Loading…</p>;
}
